/** /zh/data-export — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function DataExportZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/data-export"
      documentTitle="Draftlize 的数据导出 · Draftlize"
      documentDescription="每个 Draftlize 构件（卡片、thread、document、history、refs）按需导出为纯 markdown。无锁定、无私有格式。带到 Obsidian、Notion、vim、任何地方。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">数据导出</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            你的数据，<em>可移植。</em>
          </h1>
          <p className="lede">
            直接答案：你在 Draftlize 里创建的每个构件都按需导出为纯 markdown。无锁定。无私有格式。带到 Obsidian、Notion、vim、笔记本上一个文件夹——任何地方。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>导出的</em>是什么。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">卡片</h3>
            <p>每张卡 → 一个 <code>.md</code> 文件，带 YAML frontmatter（id、kind、pack、author、created_at）+ 正文 + wikilink 边内嵌。跟 CLI 的 <code>draftlize pull</code> 输出同格式。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Thread</h3>
            <p>每个 thread → 一个 markdown 文件，按时间顺序完整对话历史。tool call 以代码块内嵌。压缩状态作为元数据保留。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Document</h3>
            <p>你生成的长文构件（design-doc、pitch-deck、blog-post、outline）作为最终 markdown，带 <code>source_snapshot</code> 显示派生自哪些卡片。</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">卡片历史</h3>
            <p>每次编辑、提升、删除保留时间戳 + 作者 + 来源消息。完整审计轨迹。</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">Refs</h3>
            <p>你上传到 <code>_refs/</code> 的每个文件原样下载。原始文件名、原始内容。</p>
          </div>
          <div className="problem-col">
            <span className="num">VI</span>
            <h3 className="serif">Memory + frame + 边</h3>
            <p>项目 <code>memory/PROJECT.md</code>、所有 frame 视图、所有卡片间边。完整图状态。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>长什么样。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个 project 一个 .zip</h3>
            <p>Settings → Export 触发。后台任务组装 zip，准备好后邮件你下载链接（典型项目 &lt;60 秒）。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">目录布局</h3>
            <p><code>cards/c-04.md</code>、<code>threads/&lt;thread-id&gt;.md</code>、<code>documents/&lt;doc-id&gt;.md</code>、<code>card_history/&lt;card-id&gt;.json</code>、<code>refs/&lt;filename&gt;</code>、<code>memory/PROJECT.md</code>、<code>frames/&lt;frame-id&gt;.json</code>、<code>edges.json</code>。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">往返导入</h3>
            <p>导出的 zip 能导回 Draftlize（或另一租户）。往返无数据丢失：id、edge、history 全保留。CI 里测试。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">如果你<em>取消。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">30 天只读</h3>
            <p>取消后账号保留只读 30 天。你仍可浏览卡片、导出、复制文本。不能创建新 thread 或调 LLM。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">最后提醒邮件</h3>
            <p>第 25 天我们邮件你"你的数据 5 天后删除，如还没导出请现在导出"。第 30 天我们删。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">删除是永久的</h3>
            <p>我们不留"软删"备份等以后收钱。删除后 7 天内数据从所有实时 DB 和备份周期消失。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Draftlize 是付费软件。我们靠有用赢续费——不靠你想走时扣留你的数据。
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            导出端点第一天就能用。注册、用送的 $5 建一个 project、试导出确认格式——想要更多再随时充值。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
